import { useCallback, useMemo, useState } from 'react';

type SortDirection = 'asc' | 'desc';

/**
 * 공통 날짜 정렬 훅
 * 처음에 한 번만 정렬하고, 정렬 방향 변경 시 배열만 뒤집음
 */
export function useDateSort<T extends { date: string }>(
  data: T[],
  getDate?: (item: T) => string,
  initialDirection: SortDirection = 'desc'
) {
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection);

  // 처음에 한 번만 내림차순으로 정렬
  const baseSorted = useMemo(() => {
    const getDateValue = getDate || ((item: T) => item.date);
    const copied = [...data];
    copied.sort((a, b) => {
      return getDateValue(b).localeCompare(getDateValue(a)); // 내림차순
    });
    return copied;
  }, [data, getDate]);

  // 정렬 방향에 따라 배열 뒤집기만 수행
  const sorted = useMemo(() => {
    if (sortDirection === 'desc') {
      return baseSorted; // 내림차순이면 그대로
    }
    return [...baseSorted].reverse(); // 오름차순이면 뒤집기
  }, [baseSorted, sortDirection]);

  const toggleSort = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  return { sorted, sortDirection, toggleSort };
}
